import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import moment from 'moment';

import { DialogService } from '@app-admin/dialogs/dialog.service';
import { CustomFormValidator } from '@app-shared/components/custom-validators';
import { UtilService } from '@app-shared/services/util.service';
import { AddOrEditService } from '@app/shared/table/addOrEdit.service';
import { Router } from '@angular/router';
import { C } from '@angular/cdk/keycodes';
import { AdminService } from '@app/admin/admin.service';
import { FOLDER_TYPES } from '@app/shared/constants/application-constants';
import { MatDatepicker } from '@angular/material/datepicker';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-upload-mou',
    templateUrl: './upload-mou.component.html',
    styleUrls: ['./upload-mou.component.css'],
    standalone: false
})
export class UploadMouComponent implements OnInit {
  @ViewChild('picker1') picker1: MatDatepicker<any>;
  @ViewChild('picker2') picker2: MatDatepicker<any>;


  mouForm: UntypedFormGroup;
  pageType: string;
  serviceError: string;
  validFileExtensions = ['png', 'jpg', 'jpeg', 'pdf'];
  agreementDateRanges = [];
  uploadedMouFile: string;
  isLoading = false;



  constructor(
    @Inject(MAT_DIALOG_DATA) public modalData: any,
    public dialogRef: MatDialogRef<UploadMouComponent>,
    private dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private util: UtilService,
    private dialogSrvc: DialogService,
    private cdr: ChangeDetectorRef,
    private route: Router,
    private adminSrvc: AdminService,
    private addOrEditService: AddOrEditService) { }

  ngOnInit(): void {
    this.pageType = 'id' in this.modalData ? 'Edit' : 'Add';
  this.agreementDateRanges = Array.isArray(this.modalData?.agreementDateRanges)
  ? this.modalData.agreementDateRanges
  : [];

this.mouForm = this.fb.group({
  agreement_start_date: [
    this.pageType === 'Edit' ? moment(this.modalData.agreement_start_date) : '',
    [Validators.required, CustomFormValidator.dateIsBetween(this.agreementDateRanges)]
  ],
  agreement_expiry_date: [
    this.pageType === 'Edit' ? moment(this.modalData.agreement_expiry_date) : '',
    [Validators.required, CustomFormValidator.dateIsBetween(this.agreementDateRanges)]
  ],
  agreement_file: [
    '',
    this.pageType === 'Add'
      ? [Validators.required, CustomFormValidator.fileExtValidator(this.validFileExtensions)]
      : []
  ],
  agreement_desc: [this.pageType === 'Edit' ? this.modalData.agreement_desc : ''],
  agreementSource: [this.pageType === 'Edit' ? this.modalData.agreement_file : '']
});

    if (this.modalData.agreement_file) {
      const fileName = this.modalData.agreement_file.replace(/.*\/MOUAgreement\//g, '');
      const splitedFileName = fileName.split('/mou/');
      if (splitedFileName.length && splitedFileName[1]) {
        this.uploadedMouFile = splitedFileName[1];
      }
    }

    if (this.pageType === 'Edit') {
      this.mouForm.get('agreement_start_date').setValidators([Validators.required]);
      this.mouForm.get('agreement_expiry_date').setValidators([Validators.required]);
    }
  }

  onFileChange(event) {
    if (event.target.files.length > 0) {
      this.uploadedMouFile = '';
      const file = event.target.files[0];
      let reader = new FileReader();
      reader.onload = () => {
        this.mouForm.patchValue({
          agreementSource: reader.result
        });

        let reqBody = {
          folder: FOLDER_TYPES.MOU,
          "file": this.mouForm.get('agreementSource').value
        }
        this.adminSrvc.uploadIimage(reqBody).subscribe(
          (data: any) => {
            const response = data.body.responseBody;
            this.mouForm.patchValue({ agreementSource: response.url });
            const responseHead = data.body.responseHead;
            const successMsg = responseHead ? this.util.getSuccessMessage('DOCUMENT_UPLOAD', responseHead.statusCode, responseHead.statusDesc) : responseHead;
            this.util.simpleDialog({
              title: 'Success',
              message: successMsg,
            });
            this.isLoading = false;
          },
          (err: any) => {
            this.isLoading = false;
            this.serviceError = err.responseHead
              ? this.util.getIntlErrorMessage("Image_upoad", err.responseHead.statusCode, err.responseHead.statusDesc)
              : err;
            this.util.simpleDialog({
              title: 'error',
              message: this.serviceError
            });
          }
        );
      };
      reader.onerror = function (error) {
        console.log('Error: ', error);
      };
      reader.readAsDataURL(file);
    }
  }
  // Add this property to your component class
  isSubmitting = false;
  
   ngAfterViewInit(): void {
    // Close any stale datepicker overlay
    Promise.resolve().then(() => {
      this.picker1?.close();
      this.picker2?.close();
    });
  }

  createMOU() {
    // Prevent duplicate submissions
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    this.uploadedMouFile = '';
    const inputs = this.mouForm.value;

    // Define payload with optional id property
    const payload: {
      organization_id: number;
      agreement_file: any;
      agreement_start_date: string;
      agreement_expiry_date: string;
      agreement_desc: string;
      id?: number;
    } = {
      organization_id: parseInt(this.modalData.organization_id || this.modalData.orgId, 10),
      agreement_file: inputs.agreementSource,
      agreement_start_date: inputs.agreement_start_date.format('YYYY-MM-DD'),
      agreement_expiry_date: inputs.agreement_expiry_date.format('YYYY-MM-DD'),
      agreement_desc: inputs.agreement_desc || ''
    };

    // Check for existing MOU
    if (!this.modalData.id && this.modalData.organization_id) {
      const existingMou = this.dialog.openDialogs[0].componentInstance.dataSource.data
        .find((item: any) => item.organization_id === payload.organization_id);
      if (existingMou) {
        payload.id = existingMou.id;
      }
    } else if (this.modalData.id) {
      payload.id = this.modalData.id;
    }

    this.util.showLoader = true;

    this.addOrEditService.addOrEditMou(payload).subscribe({
      next: (data: any) => {
        if (data?.body?.responseHead?.statusCode === '0000') {
          const title = payload.id ? 'Updated' : 'Added';
          const msgIdentifier = payload.id ? 'UPDATE' : 'ADD';
          const successMsg = this.util.getSuccessMessage(
            `${msgIdentifier}_MOU`,
            data.body.responseHead.statusCode,
            data.body.responseHead.statusDesc
          );

          this.util.simpleDialog({
            title: title,
            message: successMsg,
          });
          // Close dialog with success result
          this.closeDialog({ success: true });
        }
      },
      error: (err: any) => {
        this.serviceError = err.responseHead
          ? this.util.getIntlErrorMessage("LIST_ORGS", err.responseHead.statusCode, err.responseHead.statusDesc)
          : err;
        this.util.simpleDialog({
          title: 'Error',
          message: this.serviceError,
        });
      },
      complete: () => {
        this.isSubmitting = false;
        this.util.showLoader = false;
      }
    });
  }
  closeDialog(result?: any) {
    if (!result) {
      // User clicked cancel or close button
      result = { cancelled: true };
    }
    this.dialogRef.close(result);
  }

}